import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiTrophy, FiStar, FiTarget, FiZap, FiClock, FiAward } = FiIcons;

function AchievementModal({ isOpen, onClose, achievement }) {
  if (!isOpen || !achievement) return null;

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'from-gray-500 to-gray-600';
      case 'rare': return 'from-blue-500 to-blue-600';
      case 'epic': return 'from-purple-500 to-purple-600';
      case 'legendary': return 'from-yellow-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getRarityBorder = (rarity) => {
    switch (rarity) {
      case 'common': return 'border-gray-400/50';
      case 'rare': return 'border-blue-400/50';
      case 'epic': return 'border-purple-400/50';
      case 'legendary': return 'border-yellow-400/50';
      default: return 'border-gray-400/50';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotateY: 180 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          exit={{ opacity: 0, scale: 0.5, rotateY: 180 }}
          transition={{ duration: 0.6, type: "spring" }}
          className={`bg-gradient-to-br ${getRarityColor(achievement.rarity)} rounded-3xl p-6 w-full max-w-sm border-2 ${getRarityBorder(achievement.rarity)}`}
        >
          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-4 right-4 bg-white/20 p-2 rounded-xl"
            >
              <SafeIcon icon={FiX} className="text-white" />
            </motion.button>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              className="text-8xl mb-4"
            >
              {achievement.emoji}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2">
                Achievement Unlocked!
              </h2>
              <h3 className="text-xl font-bold text-white mb-2">
                {achievement.name}
              </h3>
              <p className="text-white/80 text-sm mb-4">
                {achievement.description}
              </p>
              
              <div className="bg-white/20 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <SafeIcon icon={FiStar} className="text-yellow-400" />
                  <span className="text-white font-medium">
                    +{achievement.points} XP
                  </span>
                </div>
                <div className="text-xs text-white/60 uppercase tracking-wide">
                  {achievement.rarity} Achievement
                </div>
              </div>

              {achievement.reward && (
                <div className="bg-yellow-400/20 border border-yellow-400/30 rounded-xl p-3">
                  <div className="flex items-center justify-center gap-2">
                    <SafeIcon icon={FiAward} className="text-yellow-400" />
                    <span className="text-yellow-200 text-sm font-medium">
                      Reward: {achievement.reward}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default AchievementModal;